import React, { useMemo } from "react";

import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router";

import RouteDrawer, { RouteDrawerProps } from "components/RouteDrawer";
import makeStyles from "hooks/makeStyles";

import useCart from "hooks/useCart";
import useMenu from "hooks/useMenu";
import { trackingEvent } from "utility/tracking";

import MenuItemModal from "./MenuItemModal";

const useStyles = makeStyles(theme => ({
	root: {},
	paper: {
		borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
		backgroundColor: "rgba(33,33,33,0.7)",
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(0),
		display: "flex",
		height: "calc(var(--vh, 1vh) * 100 - 45px)",
		overflow: "hidden",
		[theme.breakpoints.up(480)]: {
			position: "absolute",
			top: "0",
			left: "0",
			maxWidth: "536px",
			margin: "auto",
			boxShadow: "none",
			padding: 0,
			borderRadius: theme.spacing(1),
			background: theme.palette.background.default
		}
	},
	backdrop: {
		backgroundColor: "rgba(0,0,0,0.1)",
		[theme.breakpoints.up(480)]: {
			backgroundColor: "transparent",
			WebkitBackdropFilter: "blur(1.8px)",
			backdropFilter: "blur(1.8px)"
		}
	},
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start"
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff"
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		marginTop: theme.spacing(2),
		fontWeight: 500,
		fontSize: 16,
		lineHeight: 1,
		letterSpacing: "0.38px"
	}
}));

type Props = Omit<RouteDrawerProps, "swipeable" | "matchRoute">;

const MenuItemDrawer = ({ className, ...otherProps }: Props) => {
	const { hash } = useLocation();
	const navigate = useNavigate();
	const classes = useStyles();
	const { isItemAvailable } = useCart();
	const { menuItemById } = useMenu();
	const mobile = useMediaQuery("(max-width:480px)");

	const close = useMemo(
		() => () => {
			if (hash.startsWith("#item:")) {
				// navigate(-1);
				navigate("#");
			}
		},
		[hash, navigate]
	);

	const props: RouteDrawerProps = useMemo(
		() =>
			Object.assign(
				{
					className: clsx(classes.root, className),
					classes: { paper: classes.paper },
					BackdropProps: {
						className: classes.backdrop
					},
					anchor: mobile ? "bottom" : "bottom",
					disableSwipeToOpen: true,
					swipeable: true,
					matchRoute: ({ location: { hash } }) => hash.startsWith("#item:"),
					onClose: close,
					onOpen: () => {}
				},
				otherProps
			),
		[className, classes.backdrop, classes.paper, classes.root, close, mobile, otherProps]
	);

	const menuItemId = hash.substr("#item:".length);
	const menuItem = menuItemById[menuItemId];

	const itemAvailable = isItemAvailable(menuItem);

	trackingEvent("openMenuItem", "itemId", menuItemId);

	return (
		<RouteDrawer {...props}>
			<MenuItemModal menuItem={menuItem} itemAvailable={itemAvailable} onClose={close} />
		</RouteDrawer>
	);
};

export default MenuItemDrawer;
